import { useState, useEffect } from 'react';

// Enums
import { OmniRiskProfilesTableKeys } from '@/enums/OmniRiskProfiles';

// Libraries
import { isEmpty } from 'lodash';

// Types
import Locale from '@/types/locale';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import { searchObjectsOnFields } from '@/utils/search';

interface UseRiskProfileSearchProps {
  profiles: FormattedFullOmniRiskProfile[];
  locale: Locale;
}

interface UseRiskProfileSearchReturn {
  query: string;
  searching: boolean;
  filteredProfiles: FormattedFullOmniRiskProfile[];
  handleSearch: (searchQuery: string) => void;
  clearSearch: () => void;
}

export const useRiskProfileSearch = ({ profiles, locale }: UseRiskProfileSearchProps): UseRiskProfileSearchReturn => {
  const [query, setQuery] = useState<string>('');
  const [searching, setSearching] = useState<boolean>(false);
  const [filteredProfiles, setFilteredProfiles] = useState<FormattedFullOmniRiskProfile[]>(profiles);

  useEffect(() => {
    if (!searching && query === '') {
      setFilteredProfiles(profiles);
    }
  }, [profiles, searching, query]);

  const clearSearch = () => {
    setFilteredProfiles(profiles);
    setSearching(false);
    setQuery('');
  };

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    setSearching(true);

    if (isEmpty(searchQuery)) {
      clearSearch();
      return;
    }

    const searchableFields = [
      `${OmniRiskProfilesTableKeys.Name}.${locale}`,
      `${OmniRiskProfilesTableKeys.Profile}.selected.title.${locale}`,
      OmniRiskProfilesTableKeys.Status,
      `${OmniRiskProfilesTableKeys.CompletedBy}.fname`,
      `${OmniRiskProfilesTableKeys.CompletedBy}.lname`,
    ];

    const searchResult = searchObjectsOnFields(searchQuery, profiles, searchableFields);

    setFilteredProfiles(searchResult);
    setSearching(false);
  };

  return {
    query,
    searching,
    filteredProfiles,
    handleSearch,
    clearSearch,
  };
};
